import { useEffect, useState, type ReactNode } from 'react';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { APP_NAME } from '@amzjp/shared';
import { pazar } from '@/lib/pazar';
import { ekranTazelemeDinle, geriTusuDinle, sayfaAdi } from './navigation';
import { Sidebar } from './sidebar';
import { TopNav } from './top-nav';

/**
 * Masaüstü genişliğinde miyiz?
 *
 * Menünün mobilde çekmece, masaüstünde sabit olması yalnızca CSS ile
 * çözülemiyor: `inert` ve `aria-hidden` HTML nitelikleri, medya sorgusuyla
 * kapatılamıyor. Bu yüzden genişlik JS tarafında da biliniyor.
 *
 * Eşik `lg` (1024px) — Tailwind'deki `max-lg:` ile aynı sınır. İkisi
 * ayrışırsa menü bir aralıkta hem çekmece hem sabit davranır.
 */
function useMasaustu(): boolean {
  const [masaustu, setMasaustu] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1280px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1280px)');
    const uygula = () => setMasaustu(mq.matches);
    uygula();
    mq.addEventListener('change', uygula);
    return () => mq.removeEventListener('change', uygula);
  }, []);

  return masaustu;
}

export function AppShell({ children }: { children: ReactNode }) {
  const masaustu = useMasaustu();
  const [menuAcik, setMenuAcik] = useState(false);
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [tazeleme, setTazeleme] = useState(0);

  useEffect(() => ekranTazelemeDinle(() => setTazeleme((n) => n + 1)), []);
  useEffect(
    () =>
      geriTusuDinle(
        () => router.state.location.pathname,
        () => setTazeleme((n) => n + 1),
      ),
    [router],
  );

  // Sayfa değişince çekmece kapansın — kullanıcı gittiği yeri görsün
  useEffect(() => setMenuAcik(false), [pathname]);

  // Escape ile kapansın
  useEffect(() => {
    if (!menuAcik) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuAcik(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuAcik]);

  // Sekme başlığı: çok sekmede önce sayfa ve ülke okunsun; çıkışta yalnız yazılımın adı kalsın
  useEffect(() => {
    const ulke = pazar().ad;
    const ad = sayfaAdi(pathname);
    document.title = ad ? `${ad} · ${ulke} · ${APP_NAME}` : `${APP_NAME} · ${ulke}`;
    return () => {
      document.title = `${APP_NAME} · ${ulke}`;
    };
  }, [pathname]);

  /*
   * 28 Eyl 2026 — menü ÜSTTE (sahip: "menü barlarını üste alıp daha sade yap").
   * Geniş ekranda kenar menüsü hiç çizilmiyor; dar ekranda (<1280px) üst menü
   * sığmadığı için ☰ düğmesiyle çekmece olarak açılıyor.
   */
  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {!masaustu && <Sidebar acik={menuAcik} kapat={() => setMenuAcik(false)} />}

      {/* Çekmece perdesi — yalnızca dar ekranda ve açıkken */}
      {menuAcik && !masaustu && (
        <button
          type="button"
          aria-label="Menüyü kapat"
          onClick={() => setMenuAcik(false)}
          className="fixed inset-0 z-40 bg-black/40"
        />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <TopNav onMenuAc={() => setMenuAcik(true)} />
        {/*
          Mobilde kenar boşluğu 24px'ten 16px'e iniyor: 390px ekranda 48px
          boşluk, içeriğin sekizde biri demek. `max-lg:` olduğu için
          masaüstündeki `p-6` aynen duruyor.
        */}
        {/*
          İçerik ortalanıyor ve `--content-max` ile sınırlanıyor. Geniş
          ekranda satırların uçtan uca uzaması, gözün bir satırdan diğerine
          geçerken yerini kaybetmesine yol açıyordu; tablolar zaten kendi
          içinde yatay kayıyor, dolayısıyla bir şey kırpılmıyor.
        */}
        <main className="flex-1 overflow-y-auto px-6 pb-10 pt-6 max-lg:px-4 max-lg:pt-4">
          {/*
            §19.2 — `key` yol değişince içeriği yeniden bağlıyor, böylece
            belirme animasyonu her geçişte baştan oynuyor. Rota bileşeni zaten
            yol değişiminde yeniden bağlanıyor; buradaki anahtar fazladan bir
            maliyet getirmiyor, yalnızca animasyonu tetikliyor.

            Arama parametreleri (sayfalama, süzgeç) yolu değiştirmediği için
            tablo içinde gezinirken animasyon TEKRARLAMIYOR — orada titreme
            olurdu.
          */}
          <div
            key={`${pathname}:${tazeleme}`}
            className="sayfa-gecisi mx-auto w-full max-w-[var(--content-max)]"
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Sayfa başlığı bloğu — tüm ekranlarda aynı hizada dursun.
 *
 * Başlığın solunda ince bir aksan çubuğu var. Amacı süs değil KONUM: 82 ekranlı
 * bir uygulamada göz, sayfanın nerede başladığını her seferinde yeniden
 * aramamalı. Çubuk sabit bir çıpa veriyor ve içerik kartlarının hizasını
 * başlıkla aynı sütuna oturtuyor.
 *
 * `<h1>` korunuyor: E2E testleri sayfaları başlık ROLÜYLE buluyor.
 */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    // Dar ekranda başlık ile eylem düğmesi yan yana sığmıyor; alt alta geçiyor
    <div className="mb-5 flex items-start justify-between gap-4 max-sm:flex-col max-sm:gap-3">
      <div className="flex min-w-0 gap-2.5">
        <span className="mt-1 h-5 w-[3px] shrink-0 rounded-full bg-accent" aria-hidden="true" />
        <div className="min-w-0 space-y-0.5">
          <h1 className="text-[1.0625rem] font-semibold tracking-[-0.02em] text-fg">{title}</h1>
          {description && (
            <p className="text-[0.8125rem] leading-relaxed text-fg-muted">{description}</p>
          )}
        </div>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
